import type {
  DomainCanvas,
  DomainDocument,
  EntityId,
  ItemKind,
  JsonValue,
  KnowledgeItem,
  PageResult,
  SaveReceipt,
  UnixTimestamp,
} from "@note-app/contracts";
import type {
  Folder,
  RevisionRecord,
  RevisionOperation,
  Tag,
} from "@note-app/domain";

export type DeletionFilter = "active" | "trash" | "all";

export interface PageOptions {
  cursor?: string;
  limit?: number;
}

export interface ItemListOptions extends PageOptions {
  deletion?: DeletionFilter;
  kind?: ItemKind;
  folderId?: EntityId | null;
}

export interface EntityListOptions extends PageOptions {
  deletion?: DeletionFilter;
}

export interface ItemGetOptions {
  includeDeleted?: boolean;
}

export interface ItemUpdatePatch {
  title?: string;
  icon?: string | null;
  folderId?: EntityId | null;
  tags?: readonly EntityId[];
  properties?: Readonly<Record<string, JsonValue>>;
}

export interface FolderUpdatePatch {
  name?: string;
  parentId?: EntityId | null;
  position?: number;
}

export interface TagUpdatePatch {
  name?: string;
  color?: string | null;
}

export interface ItemRepository {
  create(item: KnowledgeItem): Promise<void>;
  get(
    id: EntityId,
    options?: ItemGetOptions,
  ): Promise<KnowledgeItem | null>;
  update(
    id: EntityId,
    patch: ItemUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<KnowledgeItem>;
  moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<KnowledgeItem>;
  restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
    fallbackFolderId?: EntityId | null,
  ): Promise<KnowledgeItem>;
  list(options?: ItemListOptions): Promise<PageResult<KnowledgeItem>>;
}

export interface StoredDocument {
  itemId: EntityId;
  /** Global item revision to use as expectedRevision on the next write. */
  revision: number;
  /** Revision at which the authoritative document payload last changed. */
  contentRevision: number;
  document: DomainDocument;
}

export interface ContentLoadOptions {
  /**
   * Authoritative content normally follows the active-item visibility rule.
   * Backup and recovery boundaries may opt in to reading content that is still
   * recoverable from the trash. Permanently purged content is never returned.
   */
  includeDeleted?: boolean;
}

export interface DocumentRepository {
  load(
    itemId: EntityId,
    options?: ContentLoadOptions,
  ): Promise<StoredDocument | null>;
  save(
    itemId: EntityId,
    document: DomainDocument,
    expectedRevision: number,
    committedAt: UnixTimestamp,
    options?: ContentSaveOptions,
  ): Promise<SaveReceipt>;
}

export interface StoredCanvas {
  itemId: EntityId;
  /** Global item revision to use as expectedRevision on the next write. */
  revision: number;
  /** Revision at which the authoritative canvas payload last changed. */
  contentRevision: number;
  canvas: DomainCanvas;
}

export interface CanvasRepository {
  load(
    itemId: EntityId,
    options?: ContentLoadOptions,
  ): Promise<StoredCanvas | null>;
  save(
    itemId: EntityId,
    canvas: DomainCanvas,
    expectedRevision: number,
    committedAt: UnixTimestamp,
    options?: ContentSaveOptions,
  ): Promise<SaveReceipt>;
}

export interface ContentSaveOptions {
  operation?: RevisionOperation;
}

export interface FolderRepository {
  create(folder: Folder): Promise<void>;
  get(id: EntityId, includeDeleted?: boolean): Promise<Folder | null>;
  update(
    id: EntityId,
    patch: FolderUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<Folder>;
  moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<Folder>;
  restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
  ): Promise<Folder>;
  list(options?: EntityListOptions): Promise<PageResult<Folder>>;
}

export interface TagRepository {
  create(tag: Tag): Promise<void>;
  get(id: EntityId, includeDeleted?: boolean): Promise<Tag | null>;
  update(
    id: EntityId,
    patch: TagUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<Tag>;
  moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<Tag>;
  restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
  ): Promise<Tag>;
  list(options?: EntityListOptions): Promise<PageResult<Tag>>;
}

export interface RevisionListOptions extends PageOptions {
  entityType?: RevisionRecord["entityType"];
}

export interface RevisionRepository {
  get(id: EntityId): Promise<RevisionRecord | null>;
  getByItemRevision(
    itemId: EntityId,
    revision: number,
  ): Promise<RevisionRecord | null>;
  list(
    itemId: EntityId,
    options?: RevisionListOptions,
  ): Promise<PageResult<RevisionRecord>>;
}

export interface TrashRetentionPolicy {
  readonly automaticCleanup: boolean;
  readonly retentionDays: number;
}

export const DEFAULT_TRASH_RETENTION_POLICY: TrashRetentionPolicy = {
  automaticCleanup: true,
  retentionDays: 30,
};

export interface PurgeResult {
  readonly itemId: EntityId;
  readonly removedRevisionCount: number;
  readonly detachedAttachmentIds: readonly EntityId[];
}

export interface TrashRepository {
  getRetentionPolicy(): Promise<TrashRetentionPolicy>;
  setRetentionPolicy(
    policy: TrashRetentionPolicy,
    updatedAt: UnixTimestamp,
  ): Promise<TrashRetentionPolicy>;
  purgeItem(
    itemId: EntityId,
    expectedRevision: number,
  ): Promise<PurgeResult>;
  purgeDeletedBefore(cutoff: UnixTimestamp): Promise<readonly PurgeResult[]>;
  empty(): Promise<readonly PurgeResult[]>;
}

export interface Repositories {
  readonly items: ItemRepository;
  readonly documents: DocumentRepository;
  readonly canvases: CanvasRepository;
  readonly folders: FolderRepository;
  readonly tags: TagRepository;
  readonly revisions: RevisionRepository;
  readonly trash: TrashRepository;
}
